import { ReactNode, memo, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import debounce from "lodash/debounce";
import ValidationWarning from "./ValidationWarning";
import { ValidationResult } from "../../types/forms";
import { KeyValuePair } from "../../types/generic";
import CustomTextInput from "./CustomTextInput";
import MultipleChoiceOptionNumericId, {
  MultipleChoiceOptionStringId,
} from "../../types/forms/MultipleChoiceOptions";
import FormQuestionOptionCategory from "../../types/forms/FormQuestionOptionCategory";
import MultiChoiceOptionItemWithCategory from "./MultiChoiceOptionItemWithCategory";
import { stringHelper } from "../../helpers";
import { t } from "i18next";
import MultiChoiceQuestionSearchField from "./MultiChoiceQuestionSearchField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { useCheckMobileScreen } from "../../hooks";
import { shouldForceVerticalLayoutOnMobile } from "../../helpers/formRadioButtonsHelper";

type FormRadioButtonsLayout = "HORIZONTAL" | "VERTICAL";

const getButtonClasses = (
  renderFormat: FormRadioButtonsLayout,
  isSelected: boolean,
  isReadOnly: boolean,
  background: "DARK" | "LIGHT",
) => {
  return cx(
    // Base layout classes
    "group relative flex items-center",
    renderFormat === "HORIZONTAL"
      ? "px-4 py-2 min-w-[80px] border rounded-md flex-shrink-0"
      : "p-3 border rounded-lg w-full",

    // Hover states
    !isSelected && !isReadOnly && background === "LIGHT"
      ? "hover:border-primary hover:shadow-md hover:ring-1 hover:ring-primary-pale hover:bg-black/[0.02]"
      : !isSelected && !isReadOnly && "hover:border-white hover:shadow-md",

    // Selected state styles
    isSelected
      ? background === "LIGHT"
        ? "border-primary bg-white shadow-md ring-1 ring-primary ring-opacity-30"
        : "border-white border-2 bg-transparent ring-1 ring-white ring-opacity-20"
      : background === "LIGHT"
        ? "border-gray-400 bg-white shadow-sm"
        : "border-white/70 border-2 border-dashed hover:border-solid hover:border-white/90",

    // Read-only state
    isReadOnly
      ? "cursor-not-allowed opacity-50"
      : !isSelected && "cursor-pointer",
  );
};

export interface FormRadioButtonsProps<
  TOption extends MultipleChoiceOptionNumericId | MultipleChoiceOptionStringId,
> {
  /** Used for the `name` attribute for the radio buttons, and as a prefix for `id` attributes, so make this unique */
  uniqueFieldName: string;
  /** The question text for this radio button group */
  fieldLabel?: string;
  /** The options to show in the list */
  values: TOption[];
  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  /** If validation has been run, this is the validity plus any errors */
  validationResult?: ValidationResult | null;
  /** The onChange event, returning the new state of the options */
  onChange(updatedValues: TOption[]): void;
  isReadOnly?: boolean;
  /** An event to call when the user may have stopped interacting with the radio list */
  onBlur?(latestAnswer: TOption[]): void | undefined;
  /** Whether to display in a vertical list or horizontal tiles. AUTO picks horizontal when labels are short; on mobile (<=768px), five or more options with more than three wide labels (>5 chars) use vertical layout. */
  layout?: FormRadioButtonsLayout | "AUTO";
  containerClassNames?: string;
  /** Used to render out categories and have the options appear under their relevant ones */
  categories?: FormQuestionOptionCategory[] | null;
  /** The background mode to determine styling */
  background?: "DARK" | "LIGHT";
  /** The maximum text length for horizontal (compact) layout when layout is AUTO or HORIZONTAL */
  maxTextLengthBeforeForcingVerticalLayout?: number;
  /** When true, keeps horizontal layout even if option text exceeds maxTextLengthBeforeForcingVerticalLayout */
  forceHorizontalLayout?: boolean;
  /** When true, shows a check icon on the selected option (always reserves icon space in vertical layout) */
  showSelectedCheckIcon?: boolean;
}

/** A form field containing multiple radio buttons */
function FormRadioButtons<
  TOption extends MultipleChoiceOptionNumericId | MultipleChoiceOptionStringId,
>({
  uniqueFieldName,
  fieldLabel,
  values,
  showValidationErrors = false,
  validationResult = null,
  onChange,
  onBlur = undefined,
  isReadOnly = false,
  layout = "AUTO", // Auto will calculate the layout based on the options' text length
  containerClassNames = "mt-3 space-y-3",
  categories,
  background = "LIGHT",
  maxTextLengthBeforeForcingVerticalLayout = 15,
  forceHorizontalLayout = false,
  showSelectedCheckIcon = false,
}: FormRadioButtonsProps<TOption>) {
  const isMobile = useCheckMobileScreen();
  const [optionCustomValues, setOptionCustomValues] = useState<
    KeyValuePair<number | string, string>[]
  >([]);
  const [displayItems, setDisplayItems] = useState<TOption[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [expandAllCategories, setExpandAllCategories] =
    useState<boolean>(false);

  // `onChange` handles state changes, `handleBlur` can be called to send the
  // current value to the API to be saved (if an onBlur event is defined)
  const handleBlur = (currentOptions: TOption[]) => {
    if (onBlur) {
      onBlur(currentOptions);
    }
  };

  // use the `debouncedHandleBlur` function to avoid sending the possibility
  // of firing multiple onBlur events (potentially causing multiple calls to the API)
  // as users change the value multiple times in quick succession
  const debouncedHandleBlur = useMemo(
    () => debounce(handleBlur, 500),
    [onBlur],
  );

  // Callback on value change, call on blur function (if one was provided)
  useEffect(() => {
    const customValues = values
      .filter((x) => x.allowCustomText && x.customText)
      .map((x) => ({
        key: x.optionId,
        value: x.customText!,
      }));

    setOptionCustomValues(customValues);
  }, [values]);

  /** Ensure that only one selected value exists in the state */
  const handleValueChange = (optionId: number | string) => {
    if (isReadOnly) return;

    const nextState = values.map((option) => ({
      ...option,
      isSelected: option.optionId === optionId,
    }));

    onChange(nextState);
    debouncedHandleBlur(nextState);
  };

  // Initial load (only has an effect if the categories are not null)
  useEffect(() => {
    if (!categories) return;
    onFilterAndSetDisplayItems(searchTerm);
  }, []);

  // On value change (only has an effect if the categories are not null)
  useEffect(() => {
    if (!categories) return;
    onFilterAndSetDisplayItems(searchTerm);
  }, [values]);

  // Default to horizontal. Vertical will be forced if the text is too long
  const calculatedLayout = layout === "AUTO" ? "HORIZONTAL" : layout;

  // On search term change (only has an effect if the categories are not null)
  const onSearch = (newSearchTerm: string) => {
    if (!categories) return;

    onFilterAndSetDisplayItems(newSearchTerm);
    setSearchTerm(newSearchTerm);

    const hasSearchTerm = newSearchTerm.trim().length > 0;
    setExpandAllCategories(hasSearchTerm);
  };

  const onFilterAndSetDisplayItems = (searchTerm: string) => {
    const newState = filterBySearchTerm(values, searchTerm);

    setDisplayItems(newState);
  };

  const filterBySearchTerm = (
    sourceItems: TOption[],
    searchTerm: string | null,
  ): TOption[] => {
    const filterBySearchTerm = searchTerm && searchTerm.trim().length > 0;

    const filteredOptions = sourceItems.filter((option) => {
      if (filterBySearchTerm) {
        const optionText = option.translateDisplayValue
          ? t(option.displayValueTranslationKeyIdentifier!)
          : option.text;
        return stringHelper.matchesSearch(optionText, searchTerm);
      }
      return true;
    });

    return filteredOptions;
  };

  const convertOptionsToRadioButtonGroup = (
    options: TOption[],
    hasCategories: boolean,
  ): ReactNode => {
    const forceVerticalOnMobile = shouldForceVerticalLayoutOnMobile(
      options,
      isMobile,
    );

    // Check if all options are short enough for compact layout
    let renderFormat: FormRadioButtonsLayout =
      calculatedLayout === "HORIZONTAL" &&
      !forceVerticalOnMobile &&
      options.every(
        (option) =>
          option.text !== null &&
          option.text.length <= maxTextLengthBeforeForcingVerticalLayout,
      )
        ? "HORIZONTAL"
        : "VERTICAL";

    if (forceHorizontalLayout) {
      renderFormat = "HORIZONTAL";
    }

    return (
      <div
        className={cx(
          "flex",
          renderFormat === "HORIZONTAL"
            ? "flex-row flex-wrap gap-3"
            : "flex-col gap-2",
        )}
      >
        {options.map(
          ({ value, text, isSelected, optionId, allowCustomText }) => {
            const itemCustomText = optionCustomValues.find(
              (x) => x.key === optionId,
            )?.value;

            const uniqueItemKey = `${value}_${optionId}`;
            const isHorizontal = renderFormat === "HORIZONTAL";
            const showIcon =
              showSelectedCheckIcon || !isHorizontal || isSelected;

            return (
              <button
                key={uniqueItemKey}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => handleValueChange(optionId)}
                className={getButtonClasses(
                  renderFormat,
                  isSelected,
                  isReadOnly,
                  background,
                )}
              >
                <div
                  className={cx(
                    "flex items-center w-full",
                    isHorizontal && !isSelected && "justify-center",
                  )}
                >
                  <span
                    className={cx(
                      "text-sm font-medium",
                      isHorizontal && !isSelected
                        ? "w-full text-center"
                        : "flex-grow",
                      background === "LIGHT"
                        ? isSelected
                          ? "text-primary font-semibold"
                          : "text-gray-700"
                        : "text-white",
                      renderFormat ? "text-center" : "text-left",
                    )}
                  >
                    {text}
                  </span>
                  {showIcon && (
                    <div
                      className={cx(
                        "min-w-[28px] flex items-center justify-end",
                        isSelected
                          ? background === "LIGHT"
                            ? "text-primary"
                            : "text-white"
                          : "text-transparent",
                      )}
                    >
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                        className="h-5 w-5"
                      />
                    </div>
                  )}
                </div>
                {isSelected && allowCustomText && (
                  <div className="absolute left-0 right-0 -bottom-1">
                    <CustomTextInput<TOption>
                      itemCustomText={itemCustomText}
                      optionId={optionId}
                      values={values}
                      onChange={onChange}
                      optionCustomValues={optionCustomValues}
                      onOptionCustomValueChange={setOptionCustomValues}
                      onBlur={debouncedHandleBlur}
                    />
                  </div>
                )}
              </button>
            );
          },
        )}
      </div>
    );
  };

  return (
    <>
      {showValidationErrors && validationResult && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={validationResult.errors}
        />
      )}
      <fieldset>
        {fieldLabel && (
          <legend className="text-sm font-medium leading-4 text-gray-800">
            {fieldLabel}
          </legend>
        )}
        <div className={containerClassNames}>
          {/* No Categories */}
          {categories == undefined && (
            <div className={containerClassNames}>
              {convertOptionsToRadioButtonGroup(values, false)}
            </div>
          )}

          {/* With Categories */}
          {categories != null && categories.length > 0 && (
            <>
              <MultiChoiceQuestionSearchField
                searchTerm={searchTerm}
                onSearch={onSearch}
              />

              <div className={containerClassNames}>
                {/* No items to show */}
                {displayItems.length === 0 && (
                  <p className="text-center text-sm">
                    <span className="italic">
                      {t("Common.EmptyState.Search")}
                    </span>
                  </p>
                )}
                {/* Items to show */}
                {displayItems.length > 0 && (
                  <>
                    {categories.map(({ id, titleTranslationKeyIdentifier }) => {
                      // Retrieve all of the options that belong to this category
                      const categoryOptions = displayItems.filter(
                        (x) => x.categoryId === id,
                      );

                      return (
                        <MultiChoiceOptionItemWithCategory
                          key={id}
                          categoryId={id}
                          titleTranslationKeyIdentifier={
                            titleTranslationKeyIdentifier
                          }
                          options={categoryOptions}
                          forceExpand={expandAllCategories}
                          getReactNodesForOptions={
                            convertOptionsToRadioButtonGroup
                          }
                        />
                      );
                    })}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </fieldset>
    </>
  );
}

export default memo(FormRadioButtons) as typeof FormRadioButtons;
